import { useState } from 'react';

export default function QRLandingPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    petType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5tkpq30o41p730735h0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          petType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-orange-50 to-pink-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center">
            <img 
              src="https://static.readdy.ai/image/8ba2fb49baf129fe47398873ffc1b622/ae217e5ef1f4a035b7b045b6b3280b3a.jpeg" 
              alt="Fluffy Pets Logo" 
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-qr-code-line text-lg"></i>
            Welcome from QR Code!
          </div>
          <h1 className="text-5xl font-black mb-4 bg-gradient-to-r from-teal-600 to-orange-600 bg-clip-text text-transparent">
            Welcome to Fluffy Pets! 🐾
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Thank you for scanning our QR code! We're excited to meet you and your furry friend. Fill out the form below to get started.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-scissors-cut-line text-3xl text-white"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Professional Grooming</h3>
            <p className="text-gray-600 text-sm">Expert care for your pet's style and hygiene</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-hotel-bed-line text-3xl text-white"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Luxury Boarding</h3>
            <p className="text-gray-600 text-sm">5-star accommodation for your beloved pet</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-heart-pulse-line text-3xl text-white"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Veterinary Care</h3>
            <p className="text-gray-600 text-sm">Comprehensive health services on-site</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-gray-800 mb-3">Get in Touch</h2>
            <p className="text-gray-600">Fill out the form and we'll contact you within 24 hours</p>
          </div>

          <form id="qr-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="petType" className="block text-sm font-semibold text-gray-700 mb-2">
                  Pet Type *
                </label>
                <select
                  id="petType"
                  name="petType"
                  value={formData.petType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select pet type</option>
                  <option value="Dog">Dog</option>
                  <option value="Cat">Cat</option>
                  <option value="Bird">Bird</option>
                  <option value="Rabbit">Rabbit</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors resize-none text-sm"
                placeholder="Tell us about your pet and what services you're interested in..."
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.message.length}/500 characters
              </div>
            </div>

            {/* Submit Status Messages */}
            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center gap-3">
                <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                <div>
                  <p className="font-semibold text-green-800">Thank you for contacting us!</p>
                  <p className="text-sm text-green-700">We'll get back to you within 24 hours.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
                <div>
                  <p className="font-semibold text-red-800">Oops! Something went wrong.</p>
                  <p className="text-sm text-red-700">Please try again or call us directly.</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-teal-500 to-orange-500 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-2xl transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line text-xl animate-spin"></i>
                  Sending...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill text-xl"></i>
                  Send Message
                </>
              )}
            </button>
          </form>
        </div>

        {/* Contact Info */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-phone-line text-xl text-teal-600"></i>
            </div>
            <h4 className="font-bold text-gray-800 mb-1">Call Us</h4>
            <p className="text-gray-600 text-sm">+1 (555) 123-4567</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-mail-line text-xl text-orange-600"></i>
            </div>
            <h4 className="font-bold text-gray-800 mb-1">Email Us</h4>
            <p className="text-gray-600 text-sm">hello@fluffypets.com</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
            <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-map-pin-line text-xl text-pink-600"></i>
            </div>
            <h4 className="font-bold text-gray-800 mb-1">Visit Us</h4>
            <p className="text-gray-600 text-sm">123 Pet Care Lane, SF</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white mt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600 text-sm">
            © 2024 Fluffy Pets. All rights reserved. | <a href="https://readdy.ai/?ref=logo" className="text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap">Powered by Readdy</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
